/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import * as storageUtils from '@/utils/storageUtils';
import { errorHandler } from './errorHandler';

// Constants

export enum LocalStorageKeys {
    ACCESS_TOKEN = 'token',
    REFRESH_TOKEN = 'refreshToken',
}

const USER = 'user';
const HTTP_STATUS = {
    UNAUTHORIZED: 401,
    INVALID_TOKEN: 498,
    SERVER_ERROR: 500,
    CLIENT_CLOSED_REQUEST: 499,
    TOO_MANY_REQUESTS: 429,
    BAD_REQUEST: 400,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
};

interface ErrorResponse {
    message: string;
    statusCode: number;
    error: string;
}

interface RefreshTokenResponse {
    access_token: string;
    refresh_token: string;
}

const createAxiosInstance = (): AxiosInstance => {
    return axios.create({
        baseURL: `/${process.env.REACT_APP_API_URL}`,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

const axiosInstance = createAxiosInstance();

// Request interceptor
const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const authToken = storageUtils.getItem(LocalStorageKeys.ACCESS_TOKEN);
    if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
};

// Response interceptor
const responseInterceptor = async (error: AxiosError): Promise<AxiosError> => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const { response } = error as AxiosError<{ error?: ErrorResponse }>;

    if (response?.status === HTTP_STATUS.UNAUTHORIZED || response?.status === HTTP_STATUS.CLIENT_CLOSED_REQUEST) {
        storageUtils.setItem('hitUrl', window.location.pathname);
        handleUnauthorized();
    } else if (response?.status === HTTP_STATUS.INVALID_TOKEN) {
        storageUtils.setItem('hitUrl', window.location.pathname);
        handleInvalidToken(originalRequest);
    } else if (response?.status === HTTP_STATUS.SERVER_ERROR) {
        handleServerError(response);
    } else if (
        response?.status === HTTP_STATUS.BAD_REQUEST ||
        response?.status === HTTP_STATUS.FORBIDDEN ||
        response?.status === HTTP_STATUS.NOT_FOUND ||
        response?.status === HTTP_STATUS.TOO_MANY_REQUESTS
    ) {
        return errorHandler(response);
    }

    return Promise.reject(error);
};

const handleUnauthorized = (): void => {
    storageUtils.removeItem([LocalStorageKeys.ACCESS_TOKEN, USER]);
    window.location.href = '/login';
};

const handleInvalidToken = async (originalRequest: AxiosRequestConfig & { _retry?: boolean }): Promise<AxiosResponse> => {
    try {
        const refreshToken = storageUtils.getItem(LocalStorageKeys.REFRESH_TOKEN);
        const newResponseDetails = await axiosInstance.post<RefreshTokenResponse>(`/token?refreshToken=${refreshToken}`);

        updateTokens(newResponseDetails.data);

        if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newResponseDetails.data.access_token}`;
        }
        return axios(originalRequest);
    } catch {
        handleUnauthorized();
        throw new Error('Token refresh failed');
    }
};

const handleServerError = (response: AxiosResponse): void => {
    const correlationId = response.headers['x-sap-correlationid'];
    window.location.href = `/error/?correlationId=${correlationId}`;
};

const updateTokens = (data: RefreshTokenResponse): void => {
    storageUtils.setItem(LocalStorageKeys.ACCESS_TOKEN, data.access_token);
    storageUtils.setItem(LocalStorageKeys.REFRESH_TOKEN, data.refresh_token);
};

axiosInstance.interceptors.request.use(requestInterceptor);
axiosInstance.interceptors.response.use(response => response, responseInterceptor);

export default axiosInstance;
